import { Control, useWatch } from 'react-hook-form';
import { FeatureValueValues, FinancesDrawerFormValues } from '../RightDrawerSchema';
import { Box, Button, DialogActions, DialogContent, DialogTitle, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FeatureDefinitionView } from '../../../../../generated/ApiClient';
import { EnhancedDialog } from '../../../../../component/EnhancedDialog';
import { FormDropdown } from '../../../../../component/form/FormDropdown';
import { DialogState } from '../../../../classification/groups/GroupPage';
import { FeatureValueInput } from '../../../../classification/components/FeatureValueInput';

export function FinancesFeaturesDialog({
  onSubmit,
  onClose,
  control,
  featureIndex,
  features = [],
  state,
}: {
  onSubmit: (selectedFeature: FeatureValueValues) => void;
  onClose: () => void;
  control: Control<FinancesDrawerFormValues>;
  featureIndex: number;
  features?: FeatureDefinitionView[];
  state: DialogState;
}) {
  const { t } = useTranslation();

  const selectedFeature = useWatch({
    control,
    name: `features.${featureIndex}`,
  });

  const currentDefinition = features.find((f) => f.id === selectedFeature.featureId);

  const existingFeatureIds =
    useWatch({
      control,
      name: 'features',
    })?.map((f) => f.featureId) || [];

  const availableFeatures = features.filter(
    (feature) =>
      !existingFeatureIds.includes(feature.id) || feature.id === selectedFeature.featureId
  );

  const featureOptions = availableFeatures.map((feature) => ({
    value: feature.id,
    label: feature.name,
  }));

  const dialogTitle =
    state === DialogState.Add
      ? t('classification.features.addFeature')
      : t('classification.features.editFeature');

  if (!currentDefinition) {
    return null;
  }

  return (
    <EnhancedDialog isDialogOpened={state !== DialogState.Closed} setIsDialogOpened={onClose}>
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={3} sx={{ paddingY: 2 }}>
          <Grid item xs={12}>
            <FormDropdown
              control={control}
              name={`features.${featureIndex}.featureId`}
              label={t('classification.features.name')}
              options={featureOptions}
              fullWidth
              disabled={state === DialogState.Edit}
            />
          </Grid>

          <Grid item xs={12}>
            <FeatureValueInput
              control={control}
              label={t('classification.features.value')}
              typeFieldName={`features.${featureIndex}.featureType`}
              valueFieldName={`features.${featureIndex}.value`}
              disabled={false}
              readOnly={false}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Box sx={{ textAlign: 'right' }}>
          <Button
            onClick={onClose}
            sx={{ border: 1, borderColor: 'black', color: 'black', marginRight: '4px' }}
          >
            {t('cancel')}
          </Button>
          <Button onClick={() => onSubmit(selectedFeature)} variant="contained">
            {t('save')}
          </Button>
        </Box>
      </DialogActions>
    </EnhancedDialog>
  );
}
